import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Send,
  Loader2,
  Sparkles,
  Maximize2,
  Minimize2,
  CheckCheck,
  CalendarDays,
  Users,
  Handshake,
  Mail,
  Copy,
  Check,
} from "lucide-react";

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" &&
      window.innerWidth <= 640
  );

  const messagesEndRef = useRef(null);

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello, I'm your ALEOA Assistant 👋\n\nI can help you with memberships, activities, events, programs, partners, and information about the association.",
      time: getTime(),
      welcome: true,
    },
  ]);

  function getTime() {
    return new Date().toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // =====================================================
  // DETECT MOBILE / DESKTOP
  // =====================================================

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 640);
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);

  // =====================================================
  // AUTO SCROLL
  // =====================================================

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages, loading]);

  // =====================================================
  // SEND MESSAGE
  // =====================================================

  async function sendMessage(customMessage = null) {
    const value = (
      customMessage !== null
        ? customMessage
        : message
    ).trim();

    if (!value || loading) return;

    const userTime = getTime();

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: value,
        time: userTime,
      },
    ]);

    setMessage("");
    setLoading(true);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: value,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data?.message ||
            "Une erreur est survenue avec l'assistant."
        );
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.message,
          time: getTime(),
        },
      ]);
    } catch (error) {
      console.error(
        "ALEOA CHAT ERROR:",
        error
      );

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            error?.message ||
            "Désolé, une erreur est survenue. Veuillez réessayer.",
          time: getTime(),
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  // =====================================================
  // ENTER
  // =====================================================

  function handleKeyDown(e) {
    if (
      e.key === "Enter" &&
      !e.shiftKey
    ) {
      e.preventDefault();
      sendMessage();
    }
  }

  // =====================================================
  // COPY MESSAGE
  // =====================================================

  async function copyMessage(
    content,
    index
  ) {
    try {
      await navigator.clipboard.writeText(
        content
      );

      setCopiedIndex(index);

      setTimeout(() => {
        setCopiedIndex(null);
      }, 1500);
    } catch (error) {
      console.error(
        "COPY ERROR:",
        error
      );
    }
  }

  // =====================================================
  // QUICK ACTIONS
  // =====================================================

  const quickActions = [
    {
      label: "Membership benefits",
      icon: Users,
      message:
        "Quels sont les avantages de l'adhésion à l'ALEOA ?",
    },
    {
      label: "Upcoming events",
      icon: CalendarDays,
      message:
        "Quelles sont les activités et événements actuels de l'ALEOA ?",
    },
    {
      label: "How to join",
      icon: Users,
      message:
        "Comment puis-je rejoindre l'ALEOA ?",
    },
    {
      label: "Contact support",
      icon: Mail,
      message:
        "Comment contacter l'ALEOA ?",
    },
    {
      label: "Our partners",
      icon: Handshake,
      message:
        "Quels sont les partenaires de l'ALEOA ?",
    },
  ];

  // =====================================================
  // ALEOA LOGO
  // =====================================================

  function AleoaLogo({
    size = "normal",
  }) {
    const sizeClass =
      size === "small"
        ? "w-[34px] h-[34px]"
        : size === "launcher"
        ? "w-[46px] h-[46px]"
        : "w-[50px] h-[50px]";

    return (
      <div
        className={`
          relative
          ${sizeClass}
          shrink-0
          rounded-full
          flex
          items-center
          justify-center
        `}
      >
        {/* subtle glow */}
        <div
          className="
            absolute
            inset-0
            rounded-full
            bg-gradient-to-br
            from-cyan-400/30
            via-violet-500/25
            to-pink-500/30
            blur-md
          "
        />

        {/* glass ring */}
        <div
          className="
            absolute
            inset-0
            rounded-full
            p-[1px]
            bg-gradient-to-br
            from-white/40
            via-cyan-300/20
            to-pink-400/40
          "
        >
          <div
            className="
              w-full
              h-full
              rounded-full
              bg-[#101117]
              border
              border-white/10
            "
          />
        </div>

        {/* official ALEOA logo */}
        <img
          src="/favicon.ico"
          alt="ALEOA"
          className="
            relative
            z-10
            w-[72%]
            h-[72%]
            object-contain
            rounded-full
          "
        />
      </div>
    );
  }

  // =====================================================
  // CLEAR / NEW CONVERSATION
  // =====================================================

  function clearConversation() {
    if (loading) return;

    setMessages([
      {
        role: "assistant",
        content:
          "Hello, I'm your ALEOA Assistant 👋\n\nI can help you with memberships, activities, events, programs, partners, and information about the association.",
        time: getTime(),
        welcome: true,
      },
    ]);

    setMessage("");
    setCopiedIndex(null);
  }

  // =====================================================
  // CLOSED CHAT BUTTON
  // =====================================================

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);

          // Mobile opens fullscreen
          if (isMobile) {
            setExpanded(false);
          }
        }}
        aria-label="Open ALEOA Assistant"
        className="
          fixed
          bottom-5
          right-5
          sm:bottom-6
          sm:right-6
          z-[9999]
          group
        "
      >
        <div
          className="
            absolute
            inset-[-5px]
            rounded-full
            bg-gradient-to-r
            from-cyan-400/20
            via-violet-500/20
            to-pink-500/20
            blur-md
            opacity-70
            group-hover:opacity-100
            transition-opacity
          "
        />

        <div
          className="
            relative
            transition-all
            duration-300
            group-hover:scale-105
            group-active:scale-95
          "
        >
          <AleoaLogo
            size="launcher"
          />

          {/* online dot */}
          <span
            className="
              absolute
              bottom-0
              right-0
              w-3
              h-3
              rounded-full
              bg-emerald-400
              border-2
              border-[#08090d]
              shadow-[0_0_8px_rgba(52,211,153,0.8)]
            "
          />
        </div>
      </button>
    );
  }

  // =====================================================
  // WINDOW SIZE
  //
  // PC:
  // expanded false = normal
  // expanded true  = big
  //
  // MOBILE:
  // expanded false = fullscreen
  // expanded true  = normal
  // =====================================================

  const windowClass = isMobile
    ? expanded
      ? `
        bottom-4
        right-4
        w-[430px]
        max-w-[calc(100vw-32px)]
        h-[680px]
        max-h-[calc(100vh-32px)]
      `
      : `
        inset-0
        w-full
        h-full
      `
    : expanded
    ? `
      inset-0
      sm:inset-5
    `
    : `
      bottom-4
      right-4
      sm:bottom-6
      sm:right-6

      w-[430px]
      max-w-[calc(100vw-32px)]

      h-[680px]
      max-h-[calc(100vh-32px)]
    `;

  // =====================================================
  // CHAT WINDOW
  // =====================================================

  return (
    <div
      className={`
        fixed
        z-[9999]
        transition-all
        duration-300
        ${windowClass}
      `}
    >
      {/* =================================================
          MAIN BLACK WINDOW
      ================================================= */}

      <div
        className={`
          relative
          w-full
          h-full
          overflow-hidden

          bg-[#08090d]

          border
          border-white/[0.08]

          shadow-[0_25px_80px_rgba(0,0,0,0.65)]

          ${
            isMobile &&
            !expanded
              ? "rounded-none"
              : "rounded-[24px] sm:rounded-[28px]"
          }
        `}
      >
        {/* subtle background */}
        <div
          className="
            pointer-events-none
            absolute
            top-[-150px]
            left-1/2
            -translate-x-1/2
            w-[350px]
            h-[300px]
            rounded-full
            bg-gradient-to-br
            from-cyan-500/[0.04]
            via-violet-500/[0.05]
            to-pink-500/[0.04]
            blur-3xl
          "
        />

        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="
            relative
            h-[58px]
            px-4
            flex
            items-center
            justify-between

            border-b
            border-white/[0.06]

            bg-[#090a0f]
          "
        >
          <div className="flex items-center gap-2.5">
            <AleoaLogo size="small" />

            <div>
              <div
                className="
                  text-[13px]
                  font-semibold
                  text-white
                "
              >
                ALEOA Assistant
              </div>

              <div
                className="
                  flex
                  items-center
                  gap-1.5
                  text-[10px]
                  text-white/35
                "
              >
                <span
                  className="
                    w-1.5
                    h-1.5
                    rounded-full
                    bg-emerald-400
                  "
                />

                Online
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* NEW CONVERSATION */}
            <button
              type="button"
              onClick={
                clearConversation
              }
              className="
                w-8
                h-8
                rounded-lg
                flex
                items-center
                justify-center
                text-white/35
                hover:text-violet-300
                hover:bg-white/[0.06]
                transition
              "
              aria-label="Nouvelle conversation"
              title="Nouvelle conversation"
            >
              <Sparkles size={15} />
            </button>

            {/* =================================================
                EXTEND / MINIMIZE

                PC:
                Maximize = big
                Minimize = normal

                MOBILE:
                Fullscreen = Minimize icon
                Normal = Maximize icon
            ================================================= */}

            <button
              type="button"
              onClick={() =>
                setExpanded(
                  !expanded
                )
              }
              className="
                w-8
                h-8
                rounded-lg
                flex
                items-center
                justify-center
                text-white/35
                hover:text-white
                hover:bg-white/[0.06]
                transition
              "
              aria-label={
                expanded
                  ? "Minimize"
                  : "Maximize"
              }
              title={
                expanded
                  ? "Minimize"
                  : "Maximize"
              }
            >
              {expanded ? (
                <Minimize2
                  size={15}
                />
              ) : (
                <Maximize2
                  size={15}
                />
              )}
            </button>

            {/* CLOSE */}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setExpanded(false);
              }}
              className="
                w-8
                h-8
                rounded-lg
                flex
                items-center
                justify-center
                text-white/35
                hover:text-white
                hover:bg-white/[0.06]
                transition
              "
              aria-label="Close"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* =================================================
            MESSAGES AREA
        ================================================= */}

        <div
          className="
            absolute
            top-[58px]
            bottom-[78px]
            left-0
            right-0

            overflow-y-auto

            px-4
            sm:px-5
            py-5

            [&::-webkit-scrollbar]:w-[4px]
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-white/[0.08]
            [&::-webkit-scrollbar-thumb]:rounded-full
            hover:[&::-webkit-scrollbar-thumb]:bg-white/[0.16]

            scrollbar-thin
            scrollbar-thumb-white/10
            scrollbar-track-transparent
          "
        >
          {messages.map(
            (msg, index) => {
              const isUser =
                msg.role === "user";

              return (
                <div
                  key={index}
                  className={`
                    mb-5
                    flex
                    ${
                      isUser
                        ? "justify-end"
                        : "justify-start"
                    }
                  `}
                >
                  {!isUser && (
                    <div className="mr-2 mt-1">
                      <AleoaLogo
                        size="small"
                      />
                    </div>
                  )}

                  <div
                    className={`
                      max-w-[82%]
                      ${
                        isUser
                          ? "items-end"
                          : "items-start"
                      }
                      flex
                      flex-col
                    `}
                  >
                    {/* MESSAGE */}
                    <div
                      className={`
                        px-3.5
                        py-3
                        rounded-2xl
                        text-[13px]
                        leading-relaxed
                        whitespace-pre-wrap

                        ${
                          isUser
                            ? `
                              bg-gradient-to-br
                              from-violet-500/90
                              to-pink-500/80
                              text-white
                              rounded-br-md
                            `
                            : `
                              bg-white/[0.045]
                              border
                              border-white/[0.06]
                              text-white/75
                              rounded-bl-md
                            `
                        }

                        ${
                          msg.error
                            ? "border-red-500/30 text-red-300"
                            : ""
                        }
                      `}
                    >
                      {msg.content}
                    </div>

                    {/* COPY */}
                    {!isUser &&
                      !msg.error && (
                        <button
                          type="button"
                          onClick={() =>
                            copyMessage(
                              msg.content,
                              index
                            )
                          }
                          className="
                            mt-1.5
                            ml-1

                            flex
                            items-center
                            gap-1.5

                            px-2
                            py-1

                            rounded-md

                            text-[9px]
                            text-white/25

                            hover:text-white/60
                            hover:bg-white/[0.05]

                            transition-all
                          "
                          title="Copier"
                          aria-label="Copier le message"
                        >
                          {copiedIndex ===
                          index ? (
                            <>
                              <Check
                                size={11}
                                className="
                                  text-emerald-400
                                "
                              />

                              <span
                                className="
                                  text-emerald-400/80
                                "
                              >
                                Copied
                              </span>
                            </>
                          ) : (
                            <>
                              <Copy
                                size={11}
                              />

                              <span>
                                Copy
                              </span>
                            </>
                          )}
                        </button>
                      )}

                    {/* TIME */}
                    <div
                      className="
                        flex
                        items-center
                        gap-1
                        mt-1.5
                        px-1
                      "
                    >
                      <span
                        className="
                          text-[9px]
                          text-white/20
                        "
                      >
                        {msg.time}
                      </span>

                      {isUser && (
                        <CheckCheck
                          size={11}
                          className="
                            text-violet-300/50
                          "
                        />
                      )}
                    </div>

                    {/* QUICK ACTIONS */}
                    {msg.welcome && (
                      <div
                        className="
                          mt-4
                          flex
                          flex-wrap
                          gap-2
                        "
                      >
                        {quickActions.map(
                          (
                            action,
                            actionIndex
                          ) => {
                            const Icon =
                              action.icon;

                            return (
                              <button
                                key={
                                  actionIndex
                                }
                                type="button"
                                onClick={() =>
                                  sendMessage(
                                    action.message
                                  )
                                }
                                disabled={
                                  loading
                                }
                                className="
                                  px-3
                                  py-2

                                  rounded-full

                                  bg-white/[0.035]
                                  border
                                  border-white/[0.08]

                                  text-[10px]
                                  text-white/45

                                  hover:bg-white/[0.07]
                                  hover:text-white/80
                                  hover:border-violet-400/20

                                  transition-all
                                  duration-200

                                  flex
                                  items-center
                                  gap-1.5

                                  disabled:opacity-40
                                "
                              >
                                <Icon
                                  size={12}
                                />

                                {
                                  action.label
                                }
                              </button>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
          )}

          {/* =================================================
              TYPING
          ================================================= */}

          {loading && (
            <div className="flex justify-start mb-5">
              <div className="mr-2 mt-1">
                <AleoaLogo
                  size="small"
                />
              </div>

              <div
                className="
                  px-4
                  py-3

                  rounded-2xl
                  rounded-bl-md

                  bg-white/[0.045]
                  border
                  border-white/[0.06]

                  flex
                  items-center
                  gap-1.5
                "
              >
                <span
                  className="
                    w-1.5
                    h-1.5
                    rounded-full
                    bg-cyan-300/70
                    animate-bounce
                  "
                />

                <span
                  className="
                    w-1.5
                    h-1.5
                    rounded-full
                    bg-violet-300/70
                    animate-bounce
                    [animation-delay:150ms]
                  "
                />

                <span
                  className="
                    w-1.5
                    h-1.5
                    rounded-full
                    bg-pink-300/70
                    animate-bounce
                    [animation-delay:300ms]
                  "
                />
              </div>
            </div>
          )}

          <div
            ref={messagesEndRef}
          />
        </div>

        {/* =================================================
            INPUT
        ================================================= */}

        <div
          className="
            absolute
            bottom-0
            left-0
            right-0

            p-3

            bg-[#08090d]
            border-t
            border-white/[0.06]
          "
        >
          <div
            className="
              h-[48px]

              flex
              items-center
              gap-2

              px-1.5

              rounded-xl

              bg-[#101117]

              border
              border-white/[0.07]

              focus-within:border-violet-400/20

              transition
            "
          >
            {/* INPUT */}
            <input
              type="text"
              value={message}
              onChange={(e) =>
                setMessage(
                  e.target.value
                )
              }
              onKeyDown={
                handleKeyDown
              }
              disabled={loading}
              placeholder="Ask me anything about the association..."
              className="
                flex-1
                min-w-0

                bg-transparent
                outline-none

                text-[12px]
                text-white/80

                placeholder:text-white/25

                disabled:opacity-50
              "
            />

            {/* SEND */}
            <button
              type="button"
              onClick={() =>
                sendMessage()
              }
              disabled={
                loading ||
                !message.trim()
              }
              className="
                w-8
                h-8
                shrink-0

                rounded-lg

                flex
                items-center
                justify-center

                text-white

                bg-gradient-to-br
                from-cyan-400/70
                via-violet-500/80
                to-pink-500/80

                shadow-[0_0_15px_rgba(139,92,246,0.25)]

                hover:scale-105
                active:scale-95

                disabled:opacity-20
                disabled:hover:scale-100

                transition-all
              "
              aria-label="Send"
            >
              {loading ? (
                <Loader2
                  size={15}
                  className="animate-spin"
                />
              ) : (
                <Send
                  size={15}
                  className="
                    translate-x-[1px]
                  "
                />
              )}
            </button>
          </div>

          <p
            className="
              text-center
              text-[8px]
              text-white/15
              mt-2
            "
          >
            ALEOA Assistant
          </p>
        </div>
      </div>
    </div>
  );
}